import type {
  AssetGroupDTO,
  MemberClient,
  PositionAsset,
  PositionAssetTypeEnum,
} from "../features/api/memberApi";

const toSafeNumber = (value?: string | number | null): number => {
  const parsed = typeof value === "string" ? Number(value) : value;
  if (typeof parsed !== "number" || !Number.isFinite(parsed)) {
    return 0;
  }
  return parsed;
};

const parseAssetType = (
  assetType?: string,
): PositionAssetTypeEnum | undefined => {
  if (assetType === "TOKEN" || assetType === "STOCK") {
    return assetType as PositionAssetTypeEnum;
  }
  return undefined;
};

export type FetchOwnerPositionSummaryParams = {
  /** Omit for All holdings view; pass account id(s) for single-account filter. */
  accountIds?: string[];
  with_asset_group?: boolean;
  with_asset?: boolean;
  /** Page size per request; max 100 per API contract. */
  limit?: number;
  with_position_performance?: boolean;
  performance_type?: "PNL" | "VALUE";
  performance_period?: string;
};

type OwnerPositionSummaryApiClient = Pick<MemberClient<unknown>, "positions">;

/**
 * Paginated fetch for GET /positions/owners/summary until next_token is empty.
 * Used by Invest Portfolio (replaces getFolderAllAssets + userFolders account ids).
 */
export async function fetchOwnerPositionSummaryAllPages(
  memberApi: OwnerPositionSummaryApiClient,
  params: FetchOwnerPositionSummaryParams = {},
): Promise<AssetGroupDTO[]> {
  const {
    accountIds,
    with_asset_group = true,
    with_asset = true,
    limit = 20,
    with_position_performance,
    performance_type,
    performance_period,
  } = params;

  const allRows: AssetGroupDTO[] = [];
  let nextToken: string | undefined;

  do {
    const response = await memberApi.positions.getOwnerPositionSummary({
      ...(accountIds?.length ? { account_ids: accountIds } : {}),
      with_asset_group,
      with_asset,
      limit,
      ...(with_position_performance != null
        ? { with_position_performance }
        : {}),
      ...(performance_type ? { performance_type } : {}),
      ...(performance_period ? { performance_period } : {}),
      ...(nextToken ? { next_token: nextToken } : {}),
    });

    const page = response?.data?.data;
    if (page?.asset_groups?.length) {
      allRows.push(...page.asset_groups);
    }

    nextToken = page?.next_token || undefined;
  } while (nextToken);

  return allRows;
}

/** Map summary rows to minimal PositionAsset records for mergeContents. */
export function summaryRowsToPositionAssets(
  rows: AssetGroupDTO[],
): PositionAsset[] {
  const assets: PositionAsset[] = [];

  rows.forEach((row) => {
    const assetType = parseAssetType(row.asset_type);
    if (!row.asset_id || !assetType) {
      return;
    }
    const amount = toSafeNumber(row.total_amount);
    assets.push({
      asset_id: row.asset_id,
      asset_type: assetType,
      asset_name: row.asset_name,
      amount,
      previous_close_amount: amount,
      account_id: row.account_ids?.[0],
    });
  });

  return assets;
}

export function splitSummaryRowAssetIds(rows: AssetGroupDTO[]): {
  tokenIds: string[];
  stockIds: string[];
} {
  const tokenIds: string[] = [];
  const stockIds: string[] = [];

  rows.forEach((row) => {
    if (!row.asset_id) {
      return;
    }
    if (row.asset_type === "TOKEN") {
      tokenIds.push(row.asset_id);
      return;
    }
    if (row.asset_type === "STOCK") {
      stockIds.push(row.asset_id);
    }
  });

  return { tokenIds, stockIds };
}
